import { jsPDF } from 'jspdf';
import moment from 'moment';
import { getLatestSignatureIdApi, getSignatureApi } from '@/api/encounterNotes';
import { getClinicLogoSetting } from '@/api/myPractice';
import { getPatientById, getPatientFullName, checkCardStatus, formatAppointmentDescription } from '@/views/encounter-notes/utils/encounterNotes';
import { decodeCardClass, TYPE_LABELS, STATUS_LABELS, CARD_TYPES } from '@/views/encounter-notes/utils/actionCardUtils';
import calendarIconPng from '@/assets/encounterNotes/blackCalendar.png';
import addressIconPng from '@/assets/encounterNotes/address.png';

// ============================================================================
// PAGE CONFIGURATION
// ============================================================================

const PAGE = {
  width: 612,       // Letter size in pt (8.5")
  height: 792,      // Letter size in pt (11")
  marginLeft: 50,
  marginRight: 50,
  marginTop: 40,
  marginBottom: 50,
};

PAGE.contentWidth = PAGE.width - PAGE.marginLeft - PAGE.marginRight;

const HEADER_HEIGHT_ONE_LINE = 55;
const HEADER_HEIGHT_TWO_LINE = 55 + 14; // extra line height for wrapped address
const CONTENT_END_Y = PAGE.height - PAGE.marginBottom;

const FONT = {
  titleSize: 16,
  infoSize: 8,
  headingSize: 8,
  bodySize: 8,
  lineHeight: 14,
  sectionGap: 10,
  listIndent: 15,
  color: '#333333',
  dividerColor: '#c4c4c4',
};

// ============================================================================
// MAIN EXPORT
// ============================================================================

/**
 * Generate and download an encounter note as a PDF.
 * Only works for signed notes — unsigned notes will throw an error.
 * @param {string} patientId - Patient ID
 * @param {string} noteId - Encounter note ID
 * @param {object} store - Vuex store instance
 */
export async function generateNotePdf(patientId, store, noteData) {
  // 1. Fetch patient data and clinic logo (note data passed from caller)
  const [patient, clinicLogoSetting] = await Promise.all([
    getPatientById(patientId, store),
    fetchClinicLogoSetting(),
  ]);
  const note = noteData;
  const { logoDataUrl: clinicLogoDataUrl, logoRedirectUrl: clinicLogoRedirectUrl } = clinicLogoSetting;

  if (!note) throw new Error('Encounter note not found');

  // 2. Fetch signature (optional — skip errors for dev)
  // 2. Fetch signature
  const customerId = store.state.login?.userInfo?.customer_id;
  if (!customerId) throw new Error('Cannot determine provider for signature');

  const latestSignature = await getLatestSignatureIdApi(String(customerId));
  if (!latestSignature?.signature_id) throw new Error('Signature not found. Please set up your signature first.');

  const signatureDetails = await getSignatureApi(latestSignature.signature_id);
  if (!signatureDetails?.download_url) throw new Error('Signature image not available.');

  const signatureDataUrl = await loadImageAsDataUrl(signatureDetails.download_url);
  if (!signatureDataUrl) throw new Error('Failed to download signature image.');

  // 5. Prepare display data
  const patientName = getPatientFullName(patient) || 'Unknown Patient';
  const dob = patient?.patient_birthdate
    ? moment(patient.patient_birthdate).format('MM-DD-YYYY')
    : 'N/A';
  const serviceDate = note.service_date
    ? moment(note.service_date).format('MM-DD-YYYY')
    : '';
  const signedDate = note.signed_at
    ? formatSignedDate(note.signed_at)
    : '';

  // 6. Parse HTML content
  const contentBlocks = parseHtmlContent(note.content);

  // 6b. Fetch updated status for all action cards
  await updateCardStatuses(contentBlocks);

  // 7. Create PDF
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'pt',
    format: 'letter',
  });

  // Get practice info from account settings
  await store.dispatch('myPractice/account/getSettingData');
  const accountInfo = store.state.myPractice?.account?.old_personal || {};
  const officeAddress = accountInfo.office_address || {};
  const addressParts = [officeAddress.address_line_1, officeAddress.city, officeAddress.state, officeAddress.zip].filter(Boolean);
  const practiceAddress = addressParts.join(', ');
  const officePhone = accountInfo.contact_info?.office_phone || '';
  const headerData = { patientName, dob, serviceDate, clinicLogoDataUrl, clinicLogoRedirectUrl, practiceAddress, officePhone };

  // 8. Render first page header
  headerData.contentStartY = renderHeader(doc, headerData);

  // 9. Render content blocks with page break handling
  let currentY = headerData.contentStartY;
  currentY = renderContentBlocks(doc, contentBlocks, currentY, headerData);

  // 10. Render signature
  currentY = renderSignature(doc, signatureDataUrl, signedDate, currentY, headerData);

  // 11. Trigger download
  const safePatientName = patientName.replace(/[^a-zA-Z0-9 ]/g, '').replace(/\s+/g, '-');
  const fileDate = note.service_date
    ? moment(note.service_date).format('YYYY-MM-DD')
    : moment().format('YYYY-MM-DD');
  const fileName = `${safePatientName}-${fileDate}-EncounterNote.pdf`;
  doc.save(fileName);
}

// ============================================================================
// HEADER RENDERING
// ============================================================================

function renderHeader(doc, { patientName, dob, serviceDate, clinicLogoDataUrl, clinicLogoRedirectUrl, practiceAddress, officePhone }) {
  const y = PAGE.marginTop;

  // "ENCOUNTER NOTES" title
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(FONT.titleSize);
  doc.setTextColor(FONT.color);
  doc.text('ENCOUNTER NOTES', PAGE.marginLeft, y + 12);

  // Clinic logo in top-right corner
  if (clinicLogoDataUrl) {
    try {
      const logoMaxHeight = 20;
      const logoMaxWidth = 100;
      const logoX = PAGE.width - PAGE.marginRight - logoMaxWidth;
      doc.addImage(clinicLogoDataUrl, 'PNG', logoX, y, logoMaxWidth, logoMaxHeight);
      if (clinicLogoRedirectUrl) {
        const url = clinicLogoRedirectUrl.startsWith('http') ? clinicLogoRedirectUrl : `https://${clinicLogoRedirectUrl}`;
        doc.link(logoX, y, logoMaxWidth, logoMaxHeight, { url });
      }
    } catch (e) {
      // If logo fails to render, leave it empty
      console.error('Failed to add clinic logo to PDF:', e);
    }
  }

  // Dashed divider line below title
  const dividerY = y + 22;
  drawDashedLine(doc, PAGE.marginLeft, dividerY, PAGE.width - PAGE.marginRight, dividerY);

  // Patient info row 1: name, dob, service date
  const infoY = dividerY + 14;
  doc.setFontSize(FONT.infoSize);

  // Patient name (bold)
  doc.setFont('helvetica', 'bold');
  doc.text(patientName, PAGE.marginLeft, infoY);

  // DoB
  doc.setFont('helvetica', 'normal');
  const dobText = `DoB: ${dob}`;
  const nameWidth = doc.getTextWidth(patientName);
  const dobX = PAGE.marginLeft + nameWidth + 30;
  doc.text(dobText, dobX, infoY);

  // Calendar icon + appointment date (hide if no service date)
  const iconSize = 10;
  let nextX = dobX + doc.getTextWidth(dobText);

  // Calendar icon + service date
  if (serviceDate) {
    const iconX = nextX + 30;
    doc.addImage(calendarIconPng, 'PNG', iconX, infoY - iconSize + 2, iconSize, iconSize);
    const dateX = iconX + iconSize + 3;
    doc.text(serviceDate, dateX, infoY);
    nextX = dateX + doc.getTextWidth(serviceDate);
  }

  // Location icon + practice address and phone
  const addrIconW = 8;
  const addrIconH = 10;
  const practiceInfoParts = [practiceAddress, officePhone].filter(Boolean);
  if (practiceInfoParts.length > 0) {
    const practiceText = practiceInfoParts.join('  ');
    const gap = 30;
    const iconX = nextX + gap;
    const textX = iconX + addrIconW + 3;
    const rightEdge = PAGE.width - PAGE.marginRight;

    if (textX + doc.getTextWidth(practiceText) <= rightEdge) {
      // Fits on the same line
      doc.addImage(addressIconPng, 'PNG', iconX, infoY - addrIconH + 2, addrIconW, addrIconH);
      doc.text(practiceText, textX, infoY);
    } else {
      // Wrap to second line
      const infoY2 = infoY + FONT.lineHeight;
      doc.addImage(addressIconPng, 'PNG', PAGE.marginLeft, infoY2 - addrIconH + 2, addrIconW, addrIconH);
      doc.text(practiceText, PAGE.marginLeft + addrIconW + 3, infoY2);
      return PAGE.marginTop + HEADER_HEIGHT_TWO_LINE;
    }
  }
  return PAGE.marginTop + HEADER_HEIGHT_ONE_LINE;
}

// ============================================================================
// CONTENT RENDERING
// ============================================================================

function renderContentBlocks(doc, blocks, startY, headerData) {
  let currentY = startY;

  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i];
    const blockHeight = estimateBlockHeight(doc, block);

    // Check if we need a new page
    if (currentY + blockHeight > CONTENT_END_Y) {
      doc.addPage();
      renderHeader(doc, headerData);
      currentY = headerData.contentStartY;
    }

    currentY = renderBlock(doc, block, currentY, headerData);
  }

  return currentY;
}

function renderBlock(doc, block, y, headerData) {
  switch (block.type) {
    case 'heading':
      return renderHeading(doc, block, y);
    case 'paragraph':
      return renderParagraph(doc, block, y, headerData);
    case 'bullet':
    case 'numbered':
      return renderListItem(doc, block, y, headerData);
    case 'card':
      return renderCard(doc, block, y);
    case 'empty':
      return y + FONT.lineHeight * 0.5;
    default:
      return y;
  }
}

function renderHeading(doc, block, y) {
  y += FONT.sectionGap;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(FONT.headingSize);
  doc.setTextColor(FONT.color);
  doc.text(block.text, PAGE.marginLeft, y);
  return y + FONT.lineHeight;
}

function renderParagraph(doc, block, y, headerData) {
  doc.setFontSize(FONT.bodySize);
  doc.setTextColor(FONT.color);

  // Build full text from segments to wrap, then render with formatting
  const fullText = block.segments.map(s => s.text).join('');

  if (!fullText.trim()) {
    return y + FONT.lineHeight * 0.5;
  }

  // For simple single-style paragraphs, use splitTextToSize
  if (block.segments.length === 1) {
    const seg = block.segments[0];
    doc.setFont('helvetica', seg.bold ? 'bold' : 'normal');
    const lines = doc.splitTextToSize(fullText, PAGE.contentWidth);
    for (const line of lines) {
      if (y > CONTENT_END_Y) {
        doc.addPage();
        renderHeader(doc, headerData);
        y = headerData.contentStartY;
      }
      doc.text(line, PAGE.marginLeft, y);
      y += FONT.lineHeight;
    }
    return y;
  }

  // For mixed-format paragraphs, render segments inline
  return renderMixedSegments(doc, block.segments, PAGE.marginLeft, y, PAGE.contentWidth, headerData);
}

function renderMixedSegments(doc, segments, startX, y, maxWidth, headerData) {
  let x = startX;
  doc.setFontSize(FONT.bodySize);

  for (const seg of segments) {
    doc.setFont('helvetica', seg.bold ? 'bold' : 'normal');
    doc.setTextColor(seg.color || FONT.color);

    const words = seg.text.split(/(\s+)/);
    for (const word of words) {
      if (!word) continue;
      const wordWidth = doc.getTextWidth(word);

      // Wrap to next line if needed
      if (x + wordWidth > startX + maxWidth && x > startX) {
        x = startX;
        y += FONT.lineHeight;

        if (y > CONTENT_END_Y) {
          doc.addPage();
          renderHeader(doc, headerData);
          y = headerData.contentStartY;
        }
      }

      doc.text(word, x, y);
      x += wordWidth;
    }
  }

  return y + FONT.lineHeight;
}

function renderListItem(doc, block, y, headerData) {
  const indent = PAGE.marginLeft + (block.depth || 0) * FONT.listIndent;
  const bulletWidth = 12;
  const textX = indent + bulletWidth;
  const availableWidth = PAGE.contentWidth - ((block.depth || 0) * FONT.listIndent) - bulletWidth;

  doc.setFontSize(FONT.bodySize);
  doc.setTextColor(FONT.color);

  // Draw bullet or number
  doc.setFont('helvetica', 'normal');
  if (block.type === 'bullet') {
    const bulletChar = block.depth === 0 ? '\u2022' : block.depth === 1 ? '-' : '\u25E6';
    doc.text(bulletChar, indent, y);
  } else {
    doc.text(`${block.number}.`, indent, y);
  }

  // Render text segments
  if (block.segments && block.segments.length > 0) {
    const fullText = block.segments.map(s => s.text).join('');

    if (block.segments.length === 1) {
      const seg = block.segments[0];
      doc.setFont('helvetica', seg.bold ? 'bold' : 'normal');
      const lines = doc.splitTextToSize(fullText, availableWidth);
      for (let i = 0; i < lines.length; i++) {
        if (y > CONTENT_END_Y) {
          doc.addPage();
          renderHeader(doc, headerData);
          y = headerData.contentStartY;
        }
        doc.text(lines[i], i === 0 ? textX : textX, y);
        if (i < lines.length - 1) y += FONT.lineHeight;
      }
    } else {
      return renderMixedSegments(doc, block.segments, textX, y, availableWidth, headerData);
    }
  }

  return y + FONT.lineHeight;
}

function renderCard(doc, block, y) {
  y += 4;
  const cardHeight = 36;
  const cardX = PAGE.marginLeft;
  const cardWidth = PAGE.contentWidth;

  // Card background — red-tinted for deleted/cancelled, default otherwise
  if (block.isDeleted) {
    doc.setFillColor(250, 234, 234);
    doc.setDrawColor(209, 0, 0);
  } else {
    doc.setFillColor(249, 251, 253);
    doc.setDrawColor(196, 196, 196);
  }
  const cardTopY = y - 10;
  doc.roundedRect(cardX, cardTopY, cardWidth, cardHeight, 4, 4, 'FD');

  // Calculate vertical center for content
  const contentHeight = block.description ? 22 : 8; // title + desc or title only
  const contentStartY = cardTopY + (cardHeight - contentHeight) / 2 + 6; // +6 for text baseline

  // Card title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(FONT.bodySize);
  doc.setTextColor(FONT.color);
  const typeLabel = TYPE_LABELS[block.cardType] || block.cardType || '';
  const titleText = typeLabel ? `${typeLabel}: ${block.title}` : block.title;
  doc.text(titleText, cardX + 10, contentStartY);

  // Status badge next to title for deleted/cancelled cards
  if (block.isDeleted) {
    const titleWidth = doc.getTextWidth(titleText);
    const statusText = STATUS_LABELS[block.cardType] || 'Cancelled';
    doc.setFontSize(6);
    doc.setFont('helvetica', 'bold');
    const badgeTextWidth = doc.getTextWidth(statusText);
    const badgePadX = 5;
    const badgeX = cardX + 10 + titleWidth + 6;
    doc.setFillColor(250, 234, 234);
    doc.setDrawColor(209, 0, 0);
    doc.roundedRect(badgeX, contentStartY - 7, badgeTextWidth + badgePadX * 2, 10, 5, 5, 'FD');
    doc.setTextColor('#d10000');
    doc.text(statusText, badgeX + badgePadX, contentStartY);
  }

  // Card description
  if (block.description) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(FONT.color);
    const descLines = doc.splitTextToSize(block.description, cardWidth - 20);
    doc.text(descLines[0] || '', cardX + 10, contentStartY + 14);
  }

  return y + cardHeight + 6;
}

// ============================================================================
// SIGNATURE RENDERING
// ============================================================================

function renderSignature(doc, signatureDataUrl, signedDate, currentY, headerData) {
  const signatureHeight = 70;

  // Check if signature fits on current page
  if (currentY + signatureHeight > CONTENT_END_Y) {
    doc.addPage();
    renderHeader(doc, headerData);
    currentY = headerData.contentStartY;
  }

  // Add some spacing before signature
  currentY += 10;

  // Dashed divider
  drawDashedLine(doc, PAGE.marginLeft, currentY, PAGE.width - PAGE.marginRight, currentY);
  currentY += 15;

  // "Electronically Signed by:" + signature image
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(FONT.bodySize);
  doc.setTextColor(FONT.color);
  doc.text('Electronically Signed by:', PAGE.marginLeft, currentY);

  // Signature image
  const sigTextWidth = doc.getTextWidth('Electronically Signed by:');
  const sigImgX = PAGE.marginLeft + sigTextWidth + 8;
  const sigImgWidth = 100;
  const sigImgHeight = 36;

  try {
    doc.addImage(signatureDataUrl, 'PNG', sigImgX, currentY - 12, sigImgWidth, sigImgHeight);
  } catch (e) {
    console.error('Failed to add signature image to PDF:', e);
  }

  // Date/Time
  const dateX = sigImgX + sigImgWidth + 16;
  doc.text('Date/Time:', dateX, currentY);
  const dateLabelWidth = doc.getTextWidth('Date/Time:');
  doc.text(`  ${signedDate}`, dateX + dateLabelWidth, currentY);

  return currentY + sigImgHeight + 10;
}

// ============================================================================
// HTML CONTENT PARSING
// ============================================================================

function parseHtmlContent(htmlString) {
  if (!htmlString) return [];

  const parser = new DOMParser();
  const parsedDoc = parser.parseFromString(htmlString, 'text/html');
  const blocks = [];

  processChildren(parsedDoc.body, blocks, { listDepth: 0 });

  return blocks;
}

function processChildren(parent, blocks, context) {
  for (const node of parent.childNodes) {
    processNode(node, blocks, context);
  }
}

function processNode(node, blocks, context) {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent.trim();
    if (text) {
      blocks.push({ type: 'paragraph', segments: [{ text, bold: false, color: FONT.color }] });
    }
    return;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return;

  const tag = node.tagName.toLowerCase();

  switch (tag) {
    case 'h1':
    case 'h2':
    case 'h3':
    case 'h4':
    case 'h5':
    case 'h6':
      blocks.push({ type: 'heading', text: node.textContent.trim(), level: parseInt(tag[1]) });
      break;

    case 'p': {
      const segments = extractTextSegments(node);
      if (segments.length > 0 && segments.some(s => s.text.trim())) {
        // Check if this paragraph is entirely bold — treat as heading
        const allBold = segments.every(s => s.bold || !s.text.trim());
        if (allBold && segments.some(s => s.text.trim())) {
          blocks.push({ type: 'heading', text: segments.map(s => s.text).join('').trim() });
        } else {
          blocks.push({ type: 'paragraph', segments });
        }
      } else {
        blocks.push({ type: 'empty' });
      }
      break;
    }

    case 'ul':
      processListItems(node, 'bullet', context.listDepth || 0, blocks);
      break;

    case 'ol':
      processListItems(node, 'numbered', context.listDepth || 0, blocks);
      break;

    case 'div': {
      if (node.classList.contains('card-action-wrapper')) {
        const cardData = decodeCardClass(node.className);
        if (cardData) {
          blocks.push({
            type: 'card',
            cardType: cardData.type,
            cardId: cardData.id || null,
            calendarId: cardData.calendarId || null,
            title: cardData.title || '',
            description: cardData.description || '',
            isDeleted: false,
          });
        }
      } else {
        processChildren(node, blocks, context);
      }
      break;
    }

    case 'br':
      blocks.push({ type: 'empty' });
      break;

    default:
      processChildren(node, blocks, context);
      break;
  }
}

function processListItems(listNode, listType, depth, blocks) {
  let counter = 1;
  for (const child of listNode.children) {
    if (child.tagName.toLowerCase() !== 'li') continue;

    // Extract text segments from this li (excluding nested lists)
    const segments = [];
    for (const liChild of child.childNodes) {
      const childTag = liChild.tagName?.toLowerCase();
      if (childTag === 'ul' || childTag === 'ol') continue;
      extractSegmentsFromNode(liChild, segments, { bold: false, italic: false, color: FONT.color });
    }

    if (segments.length > 0 && segments.some(s => s.text.trim())) {
      blocks.push({
        type: listType,
        segments,
        depth,
        number: listType === 'numbered' ? counter : undefined,
      });
      counter++;
    }

    // Process nested lists
    for (const liChild of child.children) {
      const childTag = liChild.tagName.toLowerCase();
      if (childTag === 'ul') {
        processListItems(liChild, 'bullet', depth + 1, blocks);
      } else if (childTag === 'ol') {
        processListItems(liChild, 'numbered', depth + 1, blocks);
      }
    }
  }
}

function extractTextSegments(element) {
  const segments = [];
  extractSegmentsFromNode(element, segments, { bold: false, italic: false, color: FONT.color });
  return segments;
}

function extractSegmentsFromNode(node, segments, style) {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent;
    if (text) {
      segments.push({ text, bold: style.bold, italic: style.italic, color: style.color });
    }
    return;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return;

  const tag = node.tagName.toLowerCase();
  const newStyle = { ...style };

  if (tag === 'strong' || tag === 'b') newStyle.bold = true;
  if (tag === 'em' || tag === 'i') newStyle.italic = true;
  if (tag === 'span' && node.style.color) {
    newStyle.color = node.style.color;
  }

  for (const child of node.childNodes) {
    extractSegmentsFromNode(child, segments, newStyle);
  }
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Fetch updated status for all action cards in the content blocks.
 * Uses the shared checkCardStatus utility for consistent status checking.
 */
async function updateCardStatuses(blocks) {
  const cardBlocks = blocks.filter(b => b.type === 'card' && b.cardId);
  if (cardBlocks.length === 0) return;

  await Promise.all(cardBlocks.map(async (block) => {
    const { isDeleted, data } = await checkCardStatus(block.cardType, block.cardId, block.calendarId);
    if (isDeleted) {
      block.isDeleted = true;
    } else if (data && block.cardType === CARD_TYPES.APPOINTMENT) {
      block.title = data.event_title || block.title;
      block.description = formatAppointmentDescription(data) || block.description;
    }
  }));
}

function estimateBlockHeight(doc, block) {
  doc.setFontSize(FONT.bodySize);

  switch (block.type) {
    case 'heading':
      return FONT.lineHeight + FONT.sectionGap;
    case 'paragraph': {
      const text = block.segments.map(s => s.text).join('');
      const lines = doc.splitTextToSize(text, PAGE.contentWidth);
      return lines.length * FONT.lineHeight;
    }
    case 'bullet':
    case 'numbered': {
      const indent = ((block.depth || 0) * FONT.listIndent) + 12;
      const availWidth = PAGE.contentWidth - indent;
      const text = block.segments ? block.segments.map(s => s.text).join('') : '';
      const lines = doc.splitTextToSize(text, availWidth);
      return lines.length * FONT.lineHeight;
    }
    case 'card':
      return 46;
    case 'empty':
      return FONT.lineHeight * 0.5;
    default:
      return FONT.lineHeight;
  }
}

function drawDashedLine(doc, x1, y, x2, _y2) {
  doc.setDrawColor(FONT.dividerColor);
  doc.setLineWidth(0.5);

  const dashLength = 4;
  const gapLength = 3;
  let currentX = x1;

  while (currentX < x2) {
    const endX = Math.min(currentX + dashLength, x2);
    doc.line(currentX, y, endX, y);
    currentX = endX + gapLength;
  }
}

function formatSignedDate(isoString) {
  if (!isoString) return '';
  const abbr = new Date(isoString).toLocaleTimeString('en-US', { timeZoneName: 'short' }).split(' ').pop();
  return `${moment(isoString).format('MM/DD/YYYY hh:mm A')} ${abbr}`;
}

async function fetchClinicLogoSetting() {
  try {
    const res = await getClinicLogoSetting();
    const logoDataUrl = res?.data?.pns_brand_logo;
    const logoRedirectUrl = res?.data?.pns_logo_redirect_url;
    if (!logoDataUrl) return { logoDataUrl: null, logoRedirectUrl: null };
    return { logoDataUrl, logoRedirectUrl: logoRedirectUrl || null };
  } catch (e) {
    console.error('Failed to fetch clinic logo setting:', e);
    return { logoDataUrl: null, logoRedirectUrl: null };
  }
}

async function loadImageAsDataUrl(url) {
  try {
    const response = await fetch(url, { mode: 'cors' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Failed to load image via fetch, trying img fallback:', error);
    // Fallback: load via <img> tag which bypasses CORS, then use canvas
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          canvas.getContext('2d').drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/png'));
        } catch (canvasError) {
          console.error('Canvas tainted, using no-cors img:', canvasError);
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = url;
    });
  }
}
